import './TriggeredOutreachModal.scss';
// Component
import {
  Button,
  Icons,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Pagination,
  PendingContent,
  Spinner,
  Table,
} from 'plume-ui';
import { ModalStyles } from 'plume-ui/dist/components/Modal/Modal';
import { ModalBodyStyles } from 'plume-ui/dist/components/Modal/ModalBody';
import { memo, useEffect, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import { Routes } from '../../../../config';
import { outreachEventLocationsModalAtom } from '../../../../state';
import FormattedMessage from '../../../../utils/components/FormattedMessage';
// Hooks
import { AdjustableComponent } from '../../../../utils/hooks/useClasses';
import { useRedirectToRoute } from '../../../../utils/hooks/useRedirectToRoute';
import {
  useGetOutreachEventLocations,
  useOutreachEventLocationsDownloadData,
} from '../../services/monitorServices';
import {
  getOutreachEventLocationsTableHeaders,
  locationsCSVHeaders,
} from './fixtures';

type TriggeredOutreachModalProps = {
  partnerId: string;
  monitorId: string;
};

type TriggeredOutreachModalStyles = {};

const TriggeredOutreachModal: AdjustableComponent<
  TriggeredOutreachModalProps,
  TriggeredOutreachModalStyles
> = ({ partnerId, monitorId }) => {
  const redirectToRoute = useRedirectToRoute();
  const modalProps = useRecoilValue(outreachEventLocationsModalAtom);
  const resetModal = useResetRecoilState(outreachEventLocationsModalAtom);
  const [downloadData, setDownloadData] = useState([]);
  const [readyForDownload, setReadyForDownload] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const {
    outreachEventLocationsDataLoading,
    outreachEventLocationsData,
  } = useGetOutreachEventLocations(
    partnerId,
    modalProps.id,
    currentPage,
    monitorId,
  );
  const {
    outreachEventLocationsDownloadDataLoading,
    outreachEventLocationsDownloadData,
  } = useOutreachEventLocationsDownloadData(
    partnerId,
    modalProps.id,
    monitorId,
  );

  useEffect(() => {
    if (outreachEventLocationsDownloadData === undefined) return;
    if (outreachEventLocationsDownloadData?.length && !downloadData?.length) {
      setDownloadData(outreachEventLocationsDownloadData);
    }
    setReadyForDownload(
      Boolean(
        outreachEventLocationsDownloadData.length && downloadData?.length,
      ),
    );
  }, [
    outreachEventLocationsDownloadDataLoading,
    outreachEventLocationsDownloadData,
  ]);

  const handleResetModal = () => {
    setCurrentPage(0);
    resetModal();
    setReadyForDownload(false);
    setDownloadData([]);
    redirectToRoute(Routes.MonitorDetails, {
      partnerId,
      monitorId,
      eventId: '0',
    });
  };

  return (
    <Modal
      isOpen={modalProps.isOpen}
      frame
      onRequestClose={() => handleResetModal()}
      classes={(classes: ModalStyles) => {
        return {
          ...classes,
          root: `${classes.root} TriggeredOutreachModal`,
        };
      }}
    >
      <ModalHeader title={modalProps.id} />
      <ModalBody
        classes={(classes: ModalBodyStyles) => {
          return {
            ...classes,
            root: `${classes.root} TriggeredOutreachModal__body`,
          };
        }}
      >
        <CSVLink
          data={downloadData}
          target="_blank"
          className="TriggeredOutreachModal__body__download"
          headers={locationsCSVHeaders()}
          filename={`${modalProps.id}.csv`}
        >
          <Button styleVariant="secondary" disabled={!readyForDownload}>
            <Icons.DownloadIcon width={16} />
            <FormattedMessage id="actions.download" />
          </Button>
        </CSVLink>
        <div className="TriggeredOutreachModal__body__content">
          <PendingContent
            loading={outreachEventLocationsDataLoading}
            loader={Spinner}
          >
            {outreachEventLocationsData && (
              <Table
                className="TriggeredOutreachModal__body__content__table"
                tableType="standard"
                headerRow={getOutreachEventLocationsTableHeaders()}
                dataRows={outreachEventLocationsData.map((location) => ({
                  ...location,
                  health_score: location.health_score?.toFixed(0),
                }))}
              />
            )}
          </PendingContent>
        </div>
      </ModalBody>
      <ModalFooter>
        <div style={{ width: '100px' }} />
        {modalProps.locationsAmount !== undefined &&
          modalProps.locationsAmount > 10 && (
            <Pagination
              currentPage={currentPage}
              totalPageCount={Math.ceil(modalProps.locationsAmount / 10)}
              onPageSelect={(page) => {
                setCurrentPage(page);
              }}
              expandDirection="top"
            />
          )}
        <Button onClick={() => handleResetModal()} styleVariant="tertiary-grey">
          <FormattedMessage id="cancel" />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default memo(TriggeredOutreachModal);
